import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { findGameState } from "@/components/nflSchedules/findGameState";
import { GetNflScheduleQuery } from "@/graphql/gql/graphql";
import { getNflScheduleGames } from "../api";
import type { Game } from "@/components/nflSchedules/types";

const ONE_MINUTE = 60 * 1000;
export const MAX_REFETCH = 10 * ONE_MINUTE;
export const LIVE_REFETCH = 0.5 * ONE_MINUTE;

const isLiveGame = (game: Game) => {
  return findGameState(game) === "LIVE";
};

const isScheduledPregame = (
  game: Game | null
): game is Game & { scheduledTime: string } => {
  return (
    game?.status === "SCHEDULED" && game?.clock === null && !!game.scheduledTime
  );
};

const findRefetchTime = (games: GetNflScheduleQuery["getNflSchedule"]) => {
  if (!games) {
    return MAX_REFETCH;
  }
  if (games.filter((game) => game != null).some(isLiveGame)) {
    return LIVE_REFETCH;
  }
  const nextPregameGameTime = games
    .filter(isScheduledPregame)
    .sort((a, b) => {
      return a.scheduledTime > b.scheduledTime ? 1 : -1;
    })
    .at(0)?.scheduledTime;

  if (nextPregameGameTime) {
    const timeToNextGame =
      new Date(nextPregameGameTime).getTime() - new Date().getTime();
    return Math.min(MAX_REFETCH, Math.max(timeToNextGame, LIVE_REFETCH));
  }

  return MAX_REFETCH;
};

const useGetNflSchedules = (
  initialData: GetNflScheduleQuery,
  week: number | undefined
) => {
  const { data, isLoading, isError, refetch } = useQuery<GetNflScheduleQuery>({
    queryKey: ["NflSchedules", week],
    queryFn: () => getNflScheduleGames({ week }),
    initialData,
    refetchInterval: false,
    staleTime: Infinity,
    structuralSharing: false,
  });

  const weekRef = useRef<number | null>(null);
  useEffect(() => {
    if (week && week !== weekRef.current) {
      weekRef.current = week;
      refetch();
    }
  }, [week, refetch]);

  useEffect(() => {
    const timeoutId = setTimeout(
      refetch,
      findRefetchTime(data?.getNflSchedule)
    );
    return () => clearTimeout(timeoutId);
  }, [data, refetch]);

  return {
    liveSchedule: data?.getNflSchedule,
    isLoading,
    isError,
    findRefetchTime,
  };
};

export default useGetNflSchedules;
